import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  HighlightMatch,
  TextField,
  Typography,
  type AutocompleteProps,
} from '@/shared/ui';
import type { Patient } from '@/shared/types/patient';
import {
  usePatientSearch,
  MIN_PATIENT_SEARCH_LENGTH,
  type UsePatientSearchResult,
} from '../hooks/usePatientSearch';
import {
  formatPatientInputLabel,
  formatPatientOptionSecondary,
  patientDisplayName,
} from '../utils/patientOptionLabel';

type PatientSelectSlots = NonNullable<
  AutocompleteProps<Patient, false, false, false>['slots']
>;

export interface PatientSelectProps {
  value: Patient | null;
  onChange: (patient: Patient | null) => void;
  /** Prefill/edit seed when the parent only has an id. */
  seedPatientId?: string | null;
  /**
   * Parent-owned search (e.g. appointment form needs seedPending for page skeleton).
   * When omitted, PatientSelect runs `usePatientSearch` internally.
   */
  search?: UsePatientSearchResult;
  disabled?: boolean;
  required?: boolean;
  label?: string;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  id?: string;
  /** e.g. appointment quick-create paper */
  slots?: PatientSelectSlots;
  fullWidth?: boolean;
  /** Fires on every input change (incl. clear/reset) for quick-create draft text. */
  onInputValueChange?: (value: string, reason: string) => void;
}

/**
 * Standard patient Autocomplete: dropdown shows name + (code · phone) with
 * highlight; input shows `Name - code` after selection.
 */
const PatientSelect: React.FC<PatientSelectProps> = ({
  value,
  onChange,
  seedPatientId = null,
  search: searchProp,
  disabled = false,
  required = false,
  label,
  placeholder,
  error = false,
  helperText,
  id,
  slots,
  fullWidth = true,
  onInputValueChange,
}) => {
  const { t } = useTranslation(['patient', 'common']);
  const [highlightQuery, setHighlightQuery] = useState('');
  const appliedSeedRef = useRef<string | null>(null);

  const internalSearch = usePatientSearch({
    selectedPatientId: value?._id ?? '',
    seedPatientId: seedPatientId ?? value?._id ?? null,
    enabled: !searchProp,
  });
  const search = searchProp ?? internalSearch;

  // Apply seed into controlled value once (filter reopen / prefill without local fetch).
  useEffect(() => {
    if (searchProp) return;
    if (!seedPatientId) {
      appliedSeedRef.current = null;
      return;
    }
    if (value?._id === seedPatientId) {
      appliedSeedRef.current = seedPatientId;
      return;
    }
    if (appliedSeedRef.current === seedPatientId) return;
    const seeded = internalSearch.seedPatient;
    if (!seeded || seeded._id !== seedPatientId) return;
    appliedSeedRef.current = seedPatientId;
    internalSearch.rememberSelected(seeded);
    onChange(seeded);
  }, [
    searchProp,
    seedPatientId,
    value?._id,
    internalSearch.seedPatient,
    internalSearch.rememberSelected,
    onChange,
  ]);

  const resolvedLabel = label ?? t('fields.selectPatient');
  const resolvedPlaceholder = placeholder ?? t('fields.patientSearchPlaceholder');

  return (
    <Autocomplete
      id={id}
      fullWidth={fullWidth}
      forcePopupIcon={value ? false : 'auto'}
      sx={
        value
          ? {
              '&:not(:hover):not(.Mui-focused) .MuiAutocomplete-endAdornment': {
                display: 'none',
              },
              '&:not(:hover):not(.Mui-focused) .MuiOutlinedInput-root': {
                pr: '8px !important',
              },
            }
          : undefined
      }
      options={search.options}
      loading={search.searching || search.seedPending}
      loadingText={t('common:loading')}
      noOptionsText={
        highlightQuery.trim().length < MIN_PATIENT_SEARCH_LENGTH
          ? t('fields.searchTypeMore')
          : t('fields.searchNoResults')
      }
      disabled={disabled}
      filterOptions={(x) => x}
      value={value}
      slots={slots}
      getOptionLabel={formatPatientInputLabel}
      isOptionEqualToValue={(a, b) => a._id === b._id}
      onChange={(_event, next) => {
        search.rememberSelected(next);
        onChange(next);
      }}
      onInputChange={(event, next, reason) => {
        if (reason === 'input') {
          setHighlightQuery(next);
        } else if (reason === 'clear' || reason === 'reset') {
          setHighlightQuery('');
        }
        onInputValueChange?.(next, reason);
        search.handleInputChange(event, next, reason);
      }}
      onOpen={search.handleOpen}
      renderOption={(props, option) => {
        const { key, ...rest } = props as typeof props & { key?: React.Key };
        return (
          <Box component="li" key={key} {...rest}>
            <Box sx={{ minWidth: 0, py: 0.25 }}>
              <Typography variant="body2" noWrap component="div">
                <HighlightMatch
                  text={patientDisplayName(option)}
                  query={highlightQuery}
                />
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                component="div"
              >
                <HighlightMatch
                  text={formatPatientOptionSecondary(option)}
                  query={highlightQuery}
                />
              </Typography>
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={resolvedLabel}
          placeholder={resolvedPlaceholder}
          required={required}
          error={error}
          helperText={helperText}
          disabled={disabled}
        />
      )}
    />
  );
};

export default PatientSelect;
